import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, ShieldCheck, ShoppingBag, Loader2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { RelatedLinks } from "@/components/RelatedLinks";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SITE } from "@/data/site";

const JOTFORM_URL = process.env.REACT_APP_JOTFORM_MERCH_URL || "";

export default function MerchPage() {
  const [loaded, setLoaded] = useState(false);

  usePageMeta(
    "USDD Merch · Official Apparel",
    "Shop official USDD Academy merchandise — tees, hoodies, accessories and limited drops. Order directly through our merch form."
  );

  return (
    <>
      <Header />
      <main>
        <PageHero
          overline="Official Merch"
          title="Rep the"
          highlight="movement."
          description="Limited-drop tees, hoodies and accessories straight from the USDD Academy floor. Order via the form below — we'll confirm your order and arrange collection or delivery."
          ctaPrimary={{ label: "Talk to us", to: "/contact" }}
          ctaSecondary={{ label: "See classes", to: "/training" }}
          bg={SITE.classImg5}
        />

        <section className="py-16 sm:py-20 bg-black">
          <div className="max-w-5xl mx-auto px-6 sm:px-8">
            <div className="flex items-center gap-3 mb-6">
              <ShoppingBag size={16} className="text-brand-yellow" />
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-brand-yellow">
                Merch order form
              </span>
            </div>

            {JOTFORM_URL ? (
              <div className="relative rounded-3xl border border-white/10 overflow-hidden bg-white">
                {!loaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
                    <Loader2 className="text-brand-yellow animate-spin" size={28} />
                  </div>
                )}
                <iframe
                  data-testid="merch-jotform-iframe"
                  src={JOTFORM_URL}
                  title="USDD Academy merchandise order form"
                  width="100%"
                  height="720"
                  frameBorder="0"
                  onLoad={() => setLoaded(true)}
                  className="w-full"
                  style={{ minHeight: "720px", background: "white" }}
                />
                <div className="absolute top-3 right-3 z-20">
                  <a
                    href={JOTFORM_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/15 text-[10px] uppercase tracking-[0.25em] text-zinc-300 hover:text-brand-yellow"
                  >
                    Open form <ExternalLink size={11} />
                  </a>
                </div>
              </div>
            ) : (
              <div
                data-testid="merch-jotform-placeholder"
                className="relative rounded-3xl border border-brand-yellow/30 bg-gradient-to-br from-brand-purpleDeep/40 to-zinc-950 p-8 sm:p-12 overflow-hidden"
              >
                <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-brand-yellow/10 blur-3xl" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow mb-2">
                  Merch shop launching soon
                </div>
                <h2 className="font-display text-3xl sm:text-4xl text-white tracking-wide leading-tight">
                  Drop in for the next release.
                </h2>
                <p className="mt-3 text-sm text-zinc-400 max-w-xl">
                  We're wiring up the merch order form. Message us to be first in
                  line for the next limited drop.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    data-testid="merch-contact-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-xs tracking-[0.2em] hover:bg-brand-yellowHover transition-all"
                  >
                    Message us <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              <ShieldCheck size={12} /> Orders are confirmed manually — we'll email you within 24 hours.
            </div>
          </div>
        </section>

        <RelatedLinks
          title="While you're here"
          items={[
            { tag: "Schedule", label: "Book a class", desc: "Live timetable via Acuity", to: "/schedule" },
            { tag: "Events", label: "Events & Battles", desc: "Showcases & cyphers", to: "/events" },
            { tag: "Pricing", label: "Browse pricing", desc: "Drop-ins, terms & courses", to: "/pricing" },
          ]}
        />
      </main>
      <Footer />
    </>
  );
}
